import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast, ToastContainer } from "react-toastify";
import { ArrowRight, FileText, AlertTriangle, Wrench } from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { Card, CardContent } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";

interface Model {
  input_price: number;
  output_price: number;
  offpeak_input_price: number;
  offpeak_output_price: number;
  batch_input_price: number;
  batch_output_price: number;
  flex_input_price: number;
  flex_output_price: number;
  deprecation_date?: string | null;
  replacement_model?: string | null;
  [key: string]: any;
}
interface ModelList { [modelName: string]: Model }
type ExtractionLevel = "medium" | "advanced";
interface ExtractionTierInfo { price: number; description: string }
type FileExtractionInfo = Partial<Record<ExtractionLevel, ExtractionTierInfo>>;

const parsePrice = (p: number): string => (p * 1e6).toFixed(2).replace(".", ",");

const fmtCost = (p?: number): string => {
  if (p === undefined || p === null) return "-";
  if (p <= 0) return "Gratuito";
  return `R$ ${p.toFixed(3).replace(".", ",")} / página`;
};

const TIER_GROUPS: { key: string; label: string; discount?: string; in: keyof Model; out: keyof Model }[] = [
  { key: "std", label: "Padrão", in: "input_price", out: "output_price" },
  { key: "off", label: "Noturno", discount: "-30%", in: "offpeak_input_price", out: "offpeak_output_price" },
  { key: "batch", label: "Batch", discount: "-50%", in: "batch_input_price", out: "batch_output_price" },
  { key: "flex", label: "Flex", discount: "-50%", in: "flex_input_price", out: "flex_output_price" },
];

// Ferramentas integradas (Data Ocean, busca na web, execução de código): cobradas
// por uso, somadas ao custo de tokens. Disponíveis no Sabiá 4 Thinking.
const TOOL_PRICES: { name: string; unit: string; cost: string }[] = [
  { name: "Busca na web", unit: "por busca", cost: "R$ 0,0165" },
  { name: "Leitura de página", unit: "por página lida", cost: "R$ 0,066" },
  { name: "Data Ocean", unit: "por GB processado", cost: "R$ 0,10" },
  { name: "Execução de código", unit: "por minuto", cost: "R$ 0,016" },
];

export default function Pricing() {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<ModelList>({});
  const [extractionInfo, setExtractionInfo] = useState<FileExtractionInfo>({});
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const get = async () => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const headers = { Authorization: `Auth ${token}` };
        const res = await fetch(`${API_BASE}/api/chat/models`, { method: "GET", headers });
        if (!res.ok) {
          reportApiError(res, { feature: "pricing.fetch", method: "GET" });
          throw new Error("Failed");
        }
        const data = await res.json();
        setModels(data["models"] || {});
        const ex = await fetch(`${API_BASE}/api/info/file_extraction`, { method: "GET", headers });
        if (ex.ok) setExtractionInfo((await ex.json())["file_extraction_price"] || {});
        else { reportApiError(ex, { feature: "pricing.fetch", method: "GET" }); }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "pricing.fetch" });
        toast.error("Erro ao recuperar preços.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    get();
  }, [getAccessTokenSilently]);

  const visible = Object.keys(models).filter((n) => !n.toLowerCase().includes("competidor"));
  const levels: { key: ExtractionLevel; label: string }[] = [
    { key: "medium", label: "Intermediário" },
    { key: "advanced", label: "Avançado" },
  ];

  return (
    <div className="space-y-4">
      {/* Main pricing table */}
      <Card>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-[14px] font-semibold text-foreground">Preços por modelo</p>
            <p className="text-[12px] text-muted-foreground">
              Valores em R$ por 1 milhão de tokens
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th rowSpan={2} className="px-5 py-3 text-left align-middle">Modelo</th>
                {TIER_GROUPS.map((g) => (
                  <th
                    key={g.key}
                    colSpan={2}
                    className="border-l border-border px-5 py-2 text-center"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span>{g.label}</span>
                      {g.discount && (
                        <span className="rounded bg-success/15 px-1 py-0.5 text-[10px] font-medium normal-case tracking-normal text-success">
                          {g.discount}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="border-b border-border bg-muted/40 text-[10.5px] font-medium normal-case tracking-normal text-muted-foreground">
                {TIER_GROUPS.flatMap((g, i) => [
                  <th
                    key={g.key + "-in"}
                    className={cn("px-4 py-2 text-right", i > 0 && "border-l border-border")}
                  >
                    Input
                  </th>,
                  <th key={g.key + "-out"} className="px-4 py-2 text-right">
                    Output
                  </th>,
                ])}
              </tr>
            </thead>
            <tbody className="font-mono text-[13px]">
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="px-5 py-3"><Skeleton className="h-4 w-32" /></td>
                      {TIER_GROUPS.flatMap((g, j) => [
                        <td key={g.key + "-in"} className={cn("px-4 py-3", j > 0 && "border-l border-border")}>
                          <Skeleton className="ml-auto h-4 w-14" />
                        </td>,
                        <td key={g.key + "-out"} className="px-4 py-3">
                          <Skeleton className="ml-auto h-4 w-14" />
                        </td>,
                      ])}
                    </tr>
                  ))
                : visible.map((name) => {
                    const m = models[name];
                    return (
                      <tr key={name} className="border-b border-border/60 transition-colors hover:bg-muted/30">
                        <td className="px-5 py-3 font-sans font-semibold text-foreground">
                          <div className="flex items-center gap-2">
                            <span>{name}</span>
                            {m.deprecation_date && (
                              <span
                                title={`Descontinuação anunciada para ${m.deprecation_date}${m.replacement_model ? ` — usar ${m.replacement_model}` : ""}`}
                                className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning"
                              >
                                <AlertTriangle className="h-3 w-3" />
                                {m.deprecation_date.slice(8, 10)}/{m.deprecation_date.slice(5, 7)}/{m.deprecation_date.slice(0, 4)}
                              </span>
                            )}
                          </div>
                        </td>
                        {TIER_GROUPS.flatMap((g, i) => [
                          <td
                            key={g.key + "-in"}
                            className={cn("px-4 py-3 text-right text-foreground", i > 0 && "border-l border-border")}
                          >
                            {parsePrice(m[g.in] as number)}
                          </td>,
                          <td key={g.key + "-out"} className="px-4 py-3 text-right text-foreground">
                            {parsePrice(m[g.out] as number)}
                          </td>,
                        ])}
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Tier explanations */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2">
              <p className="text-[13px] font-semibold text-foreground">Noturno</p>
              <Badge variant="success">-30%</Badge>
            </div>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Tarifa reduzida automática entre 22:00 e 06:00 (BRT). Mesma API, sem mudança de
              código.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-[13px] font-semibold text-foreground">Batch</p>
                <Badge variant="success">-50%</Badge>
              </div>
              <a
                href="https://docs.maritaca.ai/pt/batch-api"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary hover:underline"
              >
                Docs
                <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Processamento assíncrono em lote. Envie um arquivo JSONL e receba os resultados.
              Ideal para grandes volumes.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-[13px] font-semibold text-foreground">Flex</p>
                <Badge variant="success">-50%</Badge>
              </div>
              <a
                href="https://docs.maritaca.ai/pt/flex-tier"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary hover:underline"
              >
                Docs
                <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <p className="text-[12px] leading-relaxed text-muted-foreground">
              Síncrono com prioridade reduzida. Mesma interface da API padrão, tolerante a
              latência maior.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Extraction table */}
      <Card>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-[14px] font-semibold text-foreground">Extração de arquivos</p>
            <p className="text-[12px] text-muted-foreground">
              Custo por página, conforme o esforço de extração
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3">Nível</th>
                <th className="px-5 py-3">Descrição</th>
                <th className="px-5 py-3 text-right">Custo</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((lv) => {
                const ex = extractionInfo?.[lv.key];
                return (
                  <tr key={lv.key} className="border-b border-border/60 last:border-0 transition-colors hover:bg-muted/30">
                    <td className="px-5 py-3 align-top">
                      <code className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[12px] text-foreground">
                        {lv.key}
                      </code>
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        {lv.label}
                      </p>
                    </td>
                    <td className="px-5 py-3 align-top text-[13px] text-foreground/90">
                      {loading ? <Skeleton className="h-4 w-full max-w-md" /> : ex?.description || "Informação indisponível"}
                    </td>
                    <td className="px-5 py-3 align-top text-right font-mono text-[13px] font-medium text-foreground">
                      {loading ? <Skeleton className="ml-auto h-4 w-32" /> : fmtCost(ex?.price)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Integrated tools */}
      <Card>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </div>
          <div>
            <p className="text-[14px] font-semibold text-foreground">Ferramentas integradas</p>
            <p className="text-[12px] text-muted-foreground">
              Cobradas por uso, somadas ao custo de tokens (disponíveis no Sabiá 4 Thinking)
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3">Ferramenta</th>
                <th className="px-5 py-3">Unidade</th>
                <th className="px-5 py-3 text-right">Custo</th>
              </tr>
            </thead>
            <tbody>
              {TOOL_PRICES.map((t) => (
                <tr key={t.name} className="border-b border-border/60 last:border-0 transition-colors hover:bg-muted/30">
                  <td className="px-5 py-3 text-[13px] font-medium text-foreground">{t.name}</td>
                  <td className="px-5 py-3 text-[13px] text-muted-foreground">{t.unit}</td>
                  <td className="px-5 py-3 text-right font-mono text-[13px] font-medium text-foreground">{t.cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ToastContainer />
    </div>
  );
}